import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Crown, Edit3, Save, Trash2, Loader2, Image as ImageIcon, Plus, X } from "lucide-react";
import ImageUploader from "../components/ImageUploader";

export default function PremiumProfilesAdmin() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // profile being edited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", category: "", description: "", cover_image: "", price_goldly: 100, instructions: "" });
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/premium/profiles").then(r => setProfiles(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const open = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      description: p.description || "",
      cover_image: p.cover_image || "",
      category: p.category || "",
      price_goldly: p.price_goldly || 100,
      instructions: p.instructions || "",
      photo_gallery: Array.isArray(p.photo_gallery) ? p.photo_gallery : [],
    });
    setMsg("");
  };

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const { data } = await api.patch(`/premium/profiles/${editing.id}`, {
        ...form,
        price_goldly: parseInt(form.price_goldly) || 0,
      });
      setMsg("✓ Saved");
      setEditing(data);
      load();
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete premium profile "${p.name}"? This also removes all its videos. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/premium/profiles/${p.id}`);
      if (editing?.id === p.id) setEditing(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const createNew = async (e) => {
    e?.preventDefault?.();
    if (!createForm.name?.trim() || !createForm.category?.trim()) { setMsg("Name and Category are required"); return; }
    setCreating(true); setMsg("");
    try {
      await api.post("/premium/profiles", { ...createForm, price_goldly: parseInt(createForm.price_goldly) || 0 });
      setCreateForm({ name: "", category: "", description: "", cover_image: "", price_goldly: 100, instructions: "" });
      setShowCreate(false);
      load();
      setMsg("✓ Created");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  if (editing) {
    return (
      <div className="px-4 py-6 pb-24" data-testid="premium-profile-edit-page">
        <button onClick={() => setEditing(null)} data-testid="ppa-back-list" className="flex items-center gap-1 text-[#00E5FF] text-sm mb-3">
          <ArrowLeft className="w-4 h-4"/>Back to all profiles
        </button>
        <h1 className="font-outfit font-extrabold text-2xl mb-1">Edit Premium Profile</h1>
        <p className="text-xs text-white/50 mb-4 truncate">{editing.id}</p>

        <div className="space-y-2">
          <ImageUploader
            label="Cover Image"
            value={form.cover_image}
            onChange={(url) => setForm(f => ({ ...f, cover_image: url }))}
            aspect="16/9"
            testId="ppa-cover-upload"
          />
          <Field label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testId="ppa-name"/>
          <Field label="Category" value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))} testId="ppa-category"/>
          <Field label="Description" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} rows={2} testId="ppa-description"/>
          <Field label="Unlock Price (Goldly)" value={form.price_goldly} onChange={v => setForm(f => ({ ...f, price_goldly: v }))} type="number" testId="ppa-price"/>
          <Field label="Instructions for viewers (what exclusive content + charges)" value={form.instructions} onChange={v => setForm(f => ({ ...f, instructions: v }))} rows={7} testId="ppa-instructions" placeholder="• 1-on-1 video call — 5000 Goldly / 15 min&#10;• Custom shoutout reel — 2000 Goldly&#10;• Personal coaching — 8000 Goldly / week"/>

          {/* Photo gallery editor (max 10) */}
          <PhotoGalleryEditor
            value={form.photo_gallery || []}
            onChange={(arr) => setForm(f => ({ ...f, photo_gallery: arr }))}
          />

          {msg && <div className={`text-xs ${msg.startsWith("✓") ? "text-emerald-300" : "text-[#FF2A5F]"}`} data-testid="ppa-msg">{msg}</div>}

          <div className="grid grid-cols-2 gap-2 pt-2">
            <button onClick={save} disabled={saving} data-testid="ppa-save"
              className="py-3 rounded-full bg-[#FF7A00] text-black font-extrabold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95">
              <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={() => remove(editing)} disabled={busy} data-testid="ppa-delete"
              className="py-3 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F] font-extrabold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95">
              <Trash2 className="w-4 h-4"/>Delete Profile
            </button>
          </div>

          <Link to={`/admin/premium-content?profile=${editing.id}`} data-testid="ppa-manage-content"
            className="block mt-3 text-center py-2.5 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] text-xs font-bold active:scale-95">
            Manage videos in this profile →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-24" data-testid="premium-profiles-admin-page">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/dashboard" data-testid="ppa-back" className="p-2 rounded-full bg-white/5"><ArrowLeft className="w-4 h-4"/></Link>
        <div className="flex-1">
          <h1 className="font-outfit font-extrabold text-2xl flex items-center gap-2">
            <Crown className="w-6 h-6 text-[#FF7A00]"/>Premium Profiles
          </h1>
          <p className="text-xs text-white/50">{profiles.length} profile{profiles.length !== 1 ? "s" : ""} · tap any to edit</p>
        </div>
      </div>

      {/* Create new profile */}
      {!showCreate ? (
        <button onClick={() => setShowCreate(true)} data-testid="ppa-new-btn"
          className="w-full py-3 mb-3 rounded-2xl bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] font-bold flex items-center justify-center gap-1.5 active:scale-95">
          <Plus className="w-4 h-4"/>Create New Premium Profile
        </button>
      ) : (
        <form onSubmit={createNew} className="mb-3 rounded-2xl bg-[#FF7A00]/8 border border-[#FF7A00]/30 p-3 space-y-2" data-testid="ppa-new-form">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-sm flex items-center gap-1.5"><Plus className="w-4 h-4 text-[#FF7A00]"/>New Premium Profile</h3>
            <button type="button" onClick={() => setShowCreate(false)} data-testid="ppa-new-cancel" className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4"/></button>
          </div>
          <ImageUploader label="Cover Image" value={createForm.cover_image} onChange={(url) => setCreateForm(f => ({ ...f, cover_image: url }))} aspect="16/9" testId="ppa-new-cover"/>
          <input data-testid="ppa-new-name" required value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="Profile name (e.g. Motivation Hub)"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <input data-testid="ppa-new-category" required value={createForm.category} onChange={e => setCreateForm(f => ({ ...f, category: e.target.value }))} placeholder="Category (e.g. Motivation / Spirituality / Fashion)"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <textarea data-testid="ppa-new-desc" rows={2} value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <input data-testid="ppa-new-price" type="number" min={0} value={createForm.price_goldly} onChange={e => setCreateForm(f => ({ ...f, price_goldly: e.target.value }))} placeholder="Unlock price (Goldly)"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          {msg && <div className={`text-xs ${msg.startsWith("✓") ? "text-emerald-300" : "text-[#FF2A5F]"}`} data-testid="ppa-new-msg">{msg}</div>}
          <button type="submit" disabled={creating} data-testid="ppa-new-submit"
            className="w-full py-2.5 rounded-full bg-[#FF7A00] text-black font-extrabold disabled:opacity-50 active:scale-95 flex items-center justify-center gap-1.5">
            <Plus className="w-4 h-4"/>{creating ? "Creating…" : "Create Profile"}
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-10 text-white/50 text-sm">No premium profiles yet.</div>
      ) : (
        <div className="space-y-2" data-testid="ppa-list">
          {profiles.map(p => (
            <button key={p.id} onClick={() => open(p)} data-testid={`ppa-row-${p.id}`}
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-[#FF7A00]/40 active:scale-[0.98] transition text-left">
              {p.cover_image ? (
                <img src={p.cover_image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0"/>
              ) : (
                <div className="w-14 h-14 rounded-xl bg-white/10 flex-shrink-0 flex items-center justify-center"><ImageIcon className="w-5 h-5 text-white/40"/></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{p.name}</div>
                <div className="text-[10px] text-white/50 flex items-center gap-1.5 mt-0.5">
                  <span className="px-1.5 py-0.5 rounded bg-[#00E5FF]/15 text-[#00E5FF]">{p.category || "—"}</span>
                  <span>·</span>
                  <span>{p.content_count || 0} videos</span>
                  <span>·</span>
                  <span className="text-[#FF7A00]">{p.price_goldly} 🪙</span>
                </div>
                {p.instructions && (
                  <div className="text-[10px] text-white/40 truncate mt-0.5">📋 {p.instructions.replace(/\n/g, " · ").slice(0, 80)}</div>
                )}
              </div>
              <Edit3 className="w-4 h-4 text-white/40 flex-shrink-0"/>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, rows, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</label>
      {rows ? (
        <textarea data-testid={testId} rows={rows} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] text-sm font-mono"/>
      ) : (
        <input data-testid={testId} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] text-sm"/>
      )}
    </div>
  );
}

function PhotoGalleryEditor({ value = [], onChange }) {
  const MAX = 10;
  const add = (url) => { if (!url || value.length >= MAX) return; onChange([...value, url]); };
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div data-testid="ppa-gallery-editor" className="space-y-1.5">
      <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Photo Gallery <span className="text-white/40 normal-case font-normal">({value.length}/{MAX})</span></label>
      <p className="text-[10px] text-white/50">Viewers see these as a sliding gallery on the profile. Max 10 photos.</p>
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="ppa-gallery-list">
          {value.map((url, i) => (
            <div key={i} data-testid={`ppa-gallery-item-${i}`} className="relative rounded-xl overflow-hidden border border-white/10 aspect-square">
              <img src={url} alt="" className="w-full h-full object-cover"/>
              <button type="button" onClick={() => removeAt(i)} data-testid={`ppa-gallery-remove-${i}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 backdrop-blur flex items-center justify-center active:scale-90">
                <span className="text-white text-xs">×</span>
              </button>
              <div className="absolute bottom-1 left-1 flex gap-1">
                {i > 0 && <button type="button" onClick={() => move(i, -1)} data-testid={`ppa-gallery-up-${i}`} className="w-5 h-5 rounded-full bg-black/60 text-[10px] active:scale-90">←</button>}
                {i < value.length - 1 && <button type="button" onClick={() => move(i, 1)} data-testid={`ppa-gallery-down-${i}`} className="w-5 h-5 rounded-full bg-black/60 text-[10px] active:scale-90">→</button>}
              </div>
              <div className="absolute bottom-1 right-1 px-1 py-0.5 rounded bg-black/70 text-[9px] font-bold">{i + 1}</div>
            </div>
          ))}
        </div>
      )}
      {value.length < MAX && (
        <ImageUploader
          label={value.length === 0 ? "Add first photo (jpg/png — uploads to gallery)" : "Add another photo"}
          value=""
          onChange={(url) => url && add(url)}
          aspect="1/1"
          testId="ppa-gallery-add"
        />
      )}
    </div>
  );
}
